// DS: CMPST16
/** A grid for displaying TopicCards. */
import React, { useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { PapiTopic } from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { MeasuredCookieType } from '@wix/da-shared-react/pkg/Measured';
import MeasuredGrid from '@wix/da-shared-react/pkg/Grid/MeasuredGrid';
import MobileNativeDropdown from '@wix/da-shared-react/pkg/Dropdown/MobileNativeDropdown';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import {
  BiContextProvidedValue,
  BiData,
  ItemClickBiEvent,
} from '@wix/da-bi/pkg/events';
import TopicCard from './TopicCard';

import s from './TopicGrid.scss';

const LARGE_CARD_GAP = parseInt(s['large-card-gap'], 10) || 24;
const SMALL_CARD_GAP = parseInt(s['small-card-gap'], 10) || 16;
const MOBILE_CARD_GAP = parseInt(s['mobile-card-gap'], 10) || 8;
const GAP_BREAKPOINT = parseInt(s['gap-breakpoint'], 10) || 1366;

export const breakpoints = [
  {
    // mobile
    maxWidth: 490,
    // 3 columns, 2 * 8px column gap, 2 * 16px gutters,
    // available space: 375 - 48 = 327 / 3 columns = 109 each
    elementWidth: 109,
    elementHeight: 109,
  },
  {
    // fix for ipad widths or it runs over right side
    maxWidth: 768,
    // 3 columns, 2 * 8px column gap, 2 * 16px gutters,
    elementWidth: 240,
    elementHeight: 240,
  },
  {
    maxWidth: 1024,
    // 4 columns, 3 * 16px column gap, 2 * 40px gutters,
    elementWidth: 224,
    elementHeight: 224,
  },
  {
    maxWidth: 1280,
    // 5 columns, 4 * 16px column gap, 2 * 40px gutters,
    elementWidth: 227,
    elementHeight: 227,
  },
  {
    maxWidth: 1366,
    // 5 columns, 4 * 16px column gap, 2 * 60px gutters,
    elementWidth: 237,
    elementHeight: 237,
  },
  {
    maxWidth: 1920,
    // 7 columns, 6 * 24px column gap, 2 * 60px gutters,
    elementWidth: 237,
    elementHeight: 237,
  },
];

export interface Props {
  /* used to see if the breakpoints defined in the spec are hit */
  screenWidth: number;
  /* array of all topics */
  allTopics: PapiTopic[];
  className?: string;
  topicClassName?: string;
  withTitle?: boolean;
}

const TopicGrid: React.FC<Props> = props => {
  const isMobile = useContext(MobileContext);
  const { t } = useTranslation();
  const {
    screenWidth,
    allTopics = [],
    className,
    topicClassName,
    withTitle = true,
  } = props;
  let spacing = screenWidth > GAP_BREAKPOINT ? LARGE_CARD_GAP : SMALL_CARD_GAP;
  if (isMobile) {
    spacing = MOBILE_CARD_GAP;
  }

  // we need a sorted list for the native select we use
  // for 'View All' on mobile
  const onSelect = useCallback(goToUrl, [allTopics]);
  const topicsAsOptions = allTopics.map(
    ({ url, name, itemId, typeId }: any) => ({
      value: url,
      label: name,
      biData: BiData<ItemClickBiEvent>({
        // used to determine which biData to fire onchange
        _value: url,
        evid: 100,
        itemid: itemId,
        sectionname: 'topTopicViewAll',
        typeid: typeId,
        rank: BiContextProvidedValue,
      }),
    })
  );
  topicsAsOptions.sort((a, b) => a.label.localeCompare(b.label));

  return (
    <ol className={classnames(s['grid'], className)}>
      <BiLoggerContextProvider value={{ sectionname: 'topicCard' }}>
        {withTitle && (
          <div className={s['top']}>
            <h2 className={s['title']}>{t('topicListPage.allTopics')}</h2>
            {isMobile && (
              <MobileNativeDropdown
                className={s['mobile-view-all']}
                title={t('topicListPage.viewAll')}
                items={topicsAsOptions}
                onSelect={onSelect}
              />
            )}
          </div>
        )}

        <MeasuredGrid
          breakpoints={breakpoints}
          elementCount={allTopics.length}
          cookieType={MeasuredCookieType.BROWSE}
          spacing={spacing}
          getElementId={String}
          renderElement={({ width, index }) => {
            const topic = allTopics[index];
            return (
              <TopicCard
                key={topic.name}
                className={classnames(s['card'], topicClassName)}
                typeId={topic.typeId}
                itemId={topic.itemId}
                name={topic.name}
                url={topic.url}
                deviations={topic.exampleDeviations}
                width={width}
                rank={index}
              />
            );
          }}
        />
      </BiLoggerContextProvider>
    </ol>
  );
};
TopicGrid.displayName = 'TopicGridCMPST16';
export default TopicGrid;

export function goToUrl(url) {
  return typeof window !== 'undefined' && window.location.assign(url);
}
